import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, UserPlus } from 'lucide-react'
import { Page } from '@/components/Page'
import { DataTable } from '@/components/DataTable'
import { Toolbar } from '@/components/Toolbar'
import { EmptyState } from '@/components/EmptyState'
import { FormField } from '@/components/FormField'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/auth/useAuth'
import { useEmployees } from '@/features/payroll/queries'
import { formatDate } from '@/lib/locale'
import type { Employee } from '@/features/payroll/types'

const STATUS_VARIANT: Record<string, 'success' | 'secondary' | 'danger' | 'warning' | 'outline'> = {
  active: 'success',
  on_leave: 'warning',
  suspended: 'warning',
  terminated: 'secondary',
  retired: 'secondary',
  deceased: 'secondary',
  pending_start: 'outline',
}

export function EmployeesListPage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading } = useEmployees({
    q: search.trim() || undefined,
    status: statusFilter || undefined,
  })
  const canManage = hasPermission('payroll.employees.manage')

  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_number',
        header: 'Employee #',
        cell: ({ row }: { row: { original: Employee } }) => (
          <Button
            variant="link"
            onClick={() => navigate(`/payroll/employees/${row.original.id}`)}
            className="h-auto justify-start p-0 font-medium"
          >
            {row.original.employee_number}
          </Button>
        ),
      },
      {
        accessorKey: 'last_name',
        header: 'Name',
        cell: ({ row }: { row: { original: Employee } }) => (
          <span>
            {row.original.first_name} {row.original.middle_name ? `${row.original.middle_name} ` : ''}
            {row.original.last_name}
          </span>
        ),
      },
      { accessorKey: 'email', header: 'Email' },
      {
        accessorKey: 'employment_type',
        header: 'Type',
        cell: ({ row }: { row: { original: Employee } }) => (
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {row.original.employment_type?.replace(/_/g, ' ')}
          </span>
        ),
      },
      { accessorKey: 'work_state', header: 'Work state' },
      {
        accessorKey: 'hire_date',
        header: 'Hired',
        cell: ({ row }: { row: { original: Employee } }) =>
          row.original.hire_date ? formatDate(row.original.hire_date) : '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: Employee } }) => (
          <Badge variant={STATUS_VARIANT[row.original.status] || 'secondary'}>
            {row.original.status.replace(/_/g, ' ')}
          </Badge>
        ),
      },
    ],
    [navigate],
  )

  const rows = data?.data ?? []

  return (
    <Page
      title="Employees"
      description="Manage W-2 employees, their employment classification, work location, and lifecycle."
      actions={
        canManage ? (
          <Button onClick={() => navigate('/payroll/employees/new')}>
            <Plus className="mr-2 size-4" /> Add employee
          </Button>
        ) : undefined
      }
    >
      <Toolbar align="start">
        <FormField
          label=""
          placeholder="Search by name, number, or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72"
        />
        <FormField
          label=""
          control={
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="on_leave">On leave</option>
              <option value="suspended">Suspended</option>
              <option value="terminated">Terminated</option>
              <option value="pending_start">Pending start</option>
            </select>
          }
        />
      </Toolbar>
      <DataTable
        columns={columns as never}
        rows={rows}
        loading={isLoading}
        empty={
          <EmptyState
            icon={UserPlus}
            title="No employees yet"
            description="Add your first employee to start running payroll. You'll capture W-4, direct deposit, and benefits during the editor flow."
            action={
              canManage ? (
                <Button onClick={() => navigate('/payroll/employees/new')}>
                  <Plus className="mr-2 size-4" /> Add employee
                </Button>
              ) : undefined
            }
          />
        }
      />
    </Page>
  )
}
